// Basic
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
// Images
import defaultBackground from '../../../../resource/images/loginMo-bg.png';
import defaultBackgroundSmall from '../../../../resource/images/loginMo-smallBg.jpg';
import defaultBackgroundPayview from '../../../../resource/images/login-background.png';
// Store & actions
import useAssetsStore from '../../store/assets';
import useSessionStore from '../../store/session';
import useCustomPayStore from '../../store/customPay';
import useDeviceSizeStore from '../../store/deviceSize';
// Common
import Logo, { LogoCustomPay } from '../../../common-utils/components/renders/logo/Logo';
import RouteUrls from '@/app/root/enums/RouteUrls';
import LocalePicker from '../../../common-utils/components/renders/localePicker/LocalePicker';
import Versions, { VersionsCustomPay } from '../../../common-utils/components/renders/versions/Versions';
import LOGIN_SCREENS from './enums/LoginScreens';
// Components
import ForgotPassword from './components/ForgotPassword/ForgotPassword';
import LoginForm from './components/LoginForm/LoginForm';
import LoginTOTP from './components/LoginTOTP/LoginTOTP';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Button } from '@/common-utils/components/ui/button';
// Css
import './Login.scss';
// Lang
import { nsErrors, nsLogin } from '@/app/i18n/namespaces';

/**
 * @returns {JSX.Element} Login Component
 */
function Login() {
    // Store
    const assets = useAssetsStore((s) => s.assets);
    const isCustomPay = useCustomPayStore((s) => s.isCustomPay);
    const isSmallDevice = useDeviceSizeStore(s => s.isSmallDevice);
    const session = useSessionStore((s) => s.session);

    // Constants
    const logoClassName = `icon-container${isSmallDevice ? ' small-device' : ''}`;

    // State
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [animationClass, setAnimationClass] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [showMfaLogin, setShowMfaLogin] = useState(false);
    const [showNoMfaAccess, setShowNoMfaAccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // Functions
    // Set animation classes between forms
    const handleRightSlide = () => {
        setAnimationClass('slide-out-right');
        setTimeout(() => {
            setAnimationClass('slide-in-left');
        }, 500);
    };
    const handleLeftSlide = () => {
        setAnimationClass('slide-out-left');
        setTimeout(() => {
            setIsForgotPassword(false);
            setShowMfaLogin(false);
            setShowNoMfaAccess(false);
            setAnimationClass('slide-in-right');
        }, 500);
    };

    const handleSlider = (screen = '') => {
        switch (screen) {
            case LOGIN_SCREENS.MAIN_LOGIN: {
                handleRightSlide();
                break;
            }
            case LOGIN_SCREENS.FORGOT_PASSWORD: {
                handleLeftSlide();
                break;
            }
            case LOGIN_SCREENS.NO_MFA_ACCESS: {
                handleLeftSlide();
                break;
            }
            case LOGIN_SCREENS.MFA_LOGIN: {
                setShowNoMfaAccess(true);
                break;
            }
            default: {
                setIsForgotPassword(true);
                handleRightSlide();
            }
        }
    };

    const getBackground = () => {
        // get small background if exist and small device detected
        if (assets?.backgroundSmall && isSmallDevice) {
            return assets?.backgroundSmall;
        }
        if (assets?.background) {
            return assets?.background;
        }
        // fallback to avoid no background
        if (!assets?.background && isCustomPay) {
            return defaultBackgroundPayview;
        }
        if (!assets?.backgroundSmall && isSmallDevice && !isCustomPay) {
            return defaultBackgroundSmall;
        }
        return defaultBackground;
    };

    const getStyle = (backgroundImageUrl) => {
        return {
            backgroundImage: isSmallDevice ? '' : `url('${backgroundImageUrl}')`,
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            backgroundPosition: '33% center',
            backgroundSize: 'cover'
        };
    };

    // Renders
    if (session) {
        return <Navigate to={RouteUrls.HOME.url} />;
    }

    return (
        <div className={`login-page${isSmallDevice ? ' small-login-page' : ''}`} style={getStyle(getBackground())}>
            <div className={`login-page-overlay${isSmallDevice ? ' small-device' : ''}`}>
                <div className='header'>
                    <LocalePicker />
                    {
                        isCustomPay ?
                            <LogoCustomPay assets={assets} className={logoClassName} />
                            : <Logo assets={assets} className={logoClassName} />
                    }
                </div>
                <div className='content'>
                    {isForgotPassword &&
                        <div className={`forgot-password-form ${animationClass}`}>
                            <ForgotPassword
                                exitScreen={handleSlider}
                            />
                        </div>
                    }
                    {!isForgotPassword &&
                        <>
                            {
                                showMfaLogin ?
                                    <div className={`login-form ${animationClass}`}>
                                        <LoginTOTP
                                            setShowMfaLogin={setShowMfaLogin}
                                            showNoMfaAccess={showNoMfaAccess}
                                            userEmail={userEmail}
                                            setSubmitError={setSubmitError}
                                            exitScreen={handleLeftSlide}
                                        />
                                    </div>
                                    :
                                    <div className={`login-form ${animationClass}`}>
                                        <LoginForm
                                            setHasMfaActivated={setShowMfaLogin}
                                            setUserEmail={setUserEmail}
                                            setSubmitError={setSubmitError}
                                            exitScreen={handleRightSlide}
                                        />
                                        {
                                            submitError &&
                                            <Entity namespace={nsErrors} entity={`api.${submitError}`} className='form login-error' />
                                        }
                                    </div>
                            }
                        </>
                    }
                </div>
                {!isForgotPassword && !showNoMfaAccess &&
                    <div className='footer pt-4'>
                        <Button
                            type='submit'
                            variant='link'
                            namespace={nsLogin}
                            entity={showMfaLogin ? 'totp.no_access_message' : 'forgotten_Pwd.title'}
                            onClick={showMfaLogin ? () => handleSlider(LOGIN_SCREENS.MFA_LOGIN) : () => handleSlider()}
                        >
                        </Button>
                    </div>}
                {isCustomPay ? <VersionsCustomPay /> : <Versions />}
            </div>
        </div >
    );
}

export default Login;